'use client';

import React from 'react';
import { motion } from 'motion/react';

import { mapBlocksToData } from '@/utilities/mapGlobalWidgetBlocksToData';
import type { GlobalWidget } from '@/payload-types';

const gradients = [
  ['#D1DEF3', '#A6BDEA'],
  ['#DED1F3', '#A6BDEA'],
  ['#FFEACF', '#A6BDEA'],
  ['#D1DEF3', '#A6BDEA'],
  ['#CEE8DC', '#A6BDEA'],
  ['#FFCECE', '#A6BDEA'],
];

export function DiseaseStatistics({ blocks }: GlobalWidget) {
  const { title, description, chart } = mapBlocksToData(blocks, {
    title: 'titleBlock',
    description: 'stringBlock',
    chart: 'features',
  } as const);

  const maxVal = Math.max(...chart.map((i) => Number(i.description)));

  return (
    <section className="v-margin" data-name="DiseaseStatistics">
      <motion.div
        className="container"
        initial="initial"
        whileInView="inView"
        viewport={{ amount: 0.5, once: true }}
      >
        <div className="grid grid-cols-[5fr_7fr] gap-6 md:gap-2 lg:grid-cols-1">
          <div className="min-h-[432px] rounded-[24px] bg-gray-700 px-8 py-[45px] md:min-h-[208px] md:px-4 md:py-[25px]">
            <motion.h2
              className="h2 text-blue mb-[25px] md:mb-[10px]"
              variants={{
                initial: { opacity: 0, y: -40 },
                inView: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.7, delay: 0.1 },
                },
              }}
            >
              {title}
            </motion.h2>
            <motion.div
              className="h7 max-w-[366px]"
              variants={{
                initial: { opacity: 0, y: -40 },
                inView: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.7, delay: 0.2 },
                },
              }}
            >
              {description}
            </motion.div>
          </div>

          <div className="grid grid-cols-6 items-end gap-4 rounded-[24px] border border-gray-500 px-8 py-[34px] sm:grid-cols-3 md:gap-x-2 md:gap-y-9 md:p-[15px] md:pb-5">
            {chart.map((i, idx) => {
              const barColors = gradients[idx % gradients.length];
              const barHeight = (i.description / maxVal) * 100;

              return (
                <div
                  className="flex flex-col gap-[14px] text-center md:gap-[11px]"
                  key={i.id}
                >
                  <div className="flex h-[312px] grow items-end md:h-[176px]">
                    <motion.div
                      className="flex w-full flex-col overflow-hidden rounded-[12px] py-[19px] md:rounded-[8px] md:py-[11px]"
                      style={{
                        background: `linear-gradient(${barColors[0]}, ${barColors[1]})`,
                      }}
                      variants={{
                        initial: { height: '0%' },
                        inView: {
                          height: `${barHeight}%`,
                          transition: {
                            delay: 0.2,
                            duration: 0.8,
                            type: 'tween',
                            ease: 'easeOut',
                            delayChildren: 0.5,
                          },
                        },
                      }}
                    >
                      <motion.div
                        className="h6 mt-auto"
                        variants={{
                          initial: { opacity: 0 },
                          inView: { opacity: 1 },
                        }}
                      >
                        {i.description}%
                      </motion.div>
                    </motion.div>
                  </div>
                  <p className="h7">{i.title}</p>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
